// Logs do Sistema — lado do front (schema `logs`, compartilhado com o Financeiro).
// Base: FINANCEIRO/financeiro-web-react/src/utils/logs.js.
//  - Interceptor global do axios: manda X-Tela (tela atual) e X-Request-Id só para as
//    APIs próprias (terceiros — CEP, S3 etc. — quebrariam no preflight de CORS).
//  - Portal de cobrança (/cobranca_cliente e /cobranca_lote_cliente, sem login): manda também X-Ator: cliente.
//  - window.onerror / unhandledrejection / ErrorBoundary → POST {urlNode}logs/front.
// Nada aqui pode quebrar a tela: tudo em try/catch e envio silencioso.
import axios from "axios";

const MAX_POR_PAGINA = 20;        // erro em loop não vira enxurrada
const JANELA_DEDUP_MS = 10000;    // mesmo erro repetido em 10 s conta uma vez
const TELAS_PORTAL = ["/cobranca_cliente", "/cobranca_lote_cliente"];

let enviados = 0;
const recentes = new Map();
let instalado = false;

function lerLocal(chave) {
  try { return localStorage.getItem(chave) || ""; } catch { return ""; }
}

/**
 * APIs próprias: Node (urlNode/urlSys), Python Operacional (urlParsed), Python Agendamento (urlAgend).
 * O portal de cobrança lê as URLs direto do process.env (EXT antes de LOCAL), então entram as duas.
 */
function urlsDaApi() {
  return [
    lerLocal("urlNode"), lerLocal("urlSys"), lerLocal("urlParsed"), lerLocal("urlAgend"),
    process.env.REACT_APP_EXT_URL1, process.env.REACT_APP_EXT_URL2, process.env.REACT_APP_EXT_URL3,
    process.env.REACT_APP_LOCAL_URL1, process.env.REACT_APP_LOCAL_URL2, process.env.REACT_APP_LOCAL_URL3,
  ].filter((u) => u && u !== "undefined");
}

function ehApiPropria(url) {
  if (!url || typeof url !== "string") return false;
  return urlsDaApi().some((base) => url.startsWith(base));
}

export function novoRequestId() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch { /* contexto não seguro (http) */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function telaAtual() {
  try { return window.location.pathname; } catch { return null; }
}

function noPortal() {
  return TELAS_PORTAL.includes(telaAtual());
}

/**
 * Envia um erro para log_erro. Usa fetch (não axios) para não passar pelo
 * interceptor nem gerar log de log.
 */
export function reportarErro({ mensagem, stack, contexto, nivel = "error", requestId } = {}) {
  try {
    const base = lerLocal("urlNode") || process.env.REACT_APP_EXT_URL3;
    if (!base || enviados >= MAX_POR_PAGINA) return;

    const msg = String(mensagem || "(sem mensagem)").slice(0, 4000);
    const chave = `${msg}|${String(stack || "").slice(0, 200)}`;
    const agora = Date.now();
    if (agora - (recentes.get(chave) || 0) < JANELA_DEDUP_MS) return;
    recentes.set(chave, agora);
    enviados++;

    const token = lerLocal("token");
    fetch(`${base}logs/front`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(noPortal() ? { "X-Ator": "cliente" } : {}),
      },
      body: JSON.stringify({
        nivel,
        mensagem: msg,
        stack: stack ? String(stack).slice(0, 20000) : null,
        contexto: contexto ?? null,
        tela: telaAtual(),
        url: window.location.href,
        request_id: requestId || null,
      }),
    }).catch(() => {});
  } catch { /* nunca propaga */ }
}

export function instalarLogs() {
  if (instalado) return;
  instalado = true;

  axios.interceptors.request.use((config) => {
    try {
      if (ehApiPropria(config.url)) {
        config.headers = config.headers || {};
        config.headers["X-Tela"] = telaAtual();
        config.headers["X-Request-Id"] = novoRequestId();
        if (noPortal()) config.headers["X-Ator"] = "cliente";
      }
    } catch { /* segue sem headers de log */ }
    return config;
  });

  window.addEventListener("error", (ev) => {
    // Erro de carregamento de recurso (img/script) não tem ev.error — ignora.
    if (!ev?.error && !ev?.message) return;
    // "Script error." = erro de script de outro domínio (widget etc.): o navegador
    // esconde mensagem e stack, não dá para agir — só ruído.
    if (ev.message === "Script error." && !ev.error?.stack) return;
    reportarErro({
      mensagem: ev.message || ev.error?.message,
      stack: ev.error?.stack,
      contexto: { tipo: "window.onerror", arquivo: ev.filename, linha: ev.lineno, coluna: ev.colno },
    });
  });

  window.addEventListener("unhandledrejection", (ev) => {
    const r = ev?.reason;
    // Erro de API já está em log_requisicao (a API grava); aqui só o que é do front.
    if (r?.isAxiosError && r?.response) return;
    reportarErro({
      mensagem: r?.message || String(r),
      stack: r?.stack,
      contexto: { tipo: "unhandledrejection", requestId: r?.config?.headers?.["X-Request-Id"] },
    });
  });
}
